/**
 * UrgencyBanner
 *
 * Sticky top bar that rotates through 3 AI-generated banner variants every 4 seconds.
 * Fetches activity data from Supabase, calls the conversion-ai edge function to
 * generate copy, then caches results in sessionStorage for 4 hours.
 *
 * Dismissible — stores dismiss state in sessionStorage so it stays hidden on
 * navigation within the same tab session.
 */

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActivityFeed {
  investorsAddedThisWeek: number;
  foundersAddedThisWeek: number;
  capitalRaisedThisMonth: string;
  discoveredConnectionsThisMonth: number;
  highActivitySectors: string[];
}

interface Banners {
  banner1: string;
  banner2: string;
  banner3: string;
}

// ---------------------------------------------------------------------------
// Cache helpers (4-hour TTL)
// ---------------------------------------------------------------------------

const CACHE_KEY = "adv3_urgency_banners";
const CACHE_TTL_MS = 4 * 60 * 60 * 1000;
const DISMISS_KEY = "adv3_urgency_dismissed";

function getCachedBanners(): Banners | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, expiresAt } = JSON.parse(raw);
    if (Date.now() > expiresAt) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data as Banners;
  } catch {
    return null;
  }
}

function setCachedBanners(data: Banners) {
  try {
    sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, expiresAt: Date.now() + CACHE_TTL_MS })
    );
  } catch {
    // sessionStorage might be unavailable — silently skip
  }
}

// ---------------------------------------------------------------------------
// Fallback copy (shown if AI call fails)
// ---------------------------------------------------------------------------

const FALLBACK_BANNERS: Banners = {
  banner1: "⭐ New investors joined this week — explore who's seeking founders like you",
  banner2: "✓ Hundreds of introductions made this month | capital raised by AdV3nture founders",
  banner3: "🚀 Trending now: SaaS & FinTech investors actively seeking early-stage founders",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UrgencyBanner = () => {
  const [banners, setBanners] = useState<Banners | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // Check dismiss state on mount
  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === "true") {
      setDismissed(true);
    }
  }, []);

  // Fetch activity data then generate banners
  const loadBanners = useCallback(async () => {
    // Check cache first
    const cached = getCachedBanners();
    if (cached) {
      setBanners(cached);
      return;
    }

    try {
      // Fetch activity feed from Supabase (public table or view)
      const { data: activity, error } = await supabase
        .from("platform_activity" as never)
        .select("*")
        .single();

      const feed: ActivityFeed = error || !activity
        ? {
            investorsAddedThisWeek: 12,
            foundersAddedThisWeek: 28,
            capitalRaisedThisMonth: "$4.2M",
            discoveredConnectionsThisMonth: 47,
            highActivitySectors: ["SaaS", "FinTech", "HealthTech"],
          }
        : (activity as unknown as ActivityFeed);

      // Call conversion-ai edge function
      const { data: aiData, error: fnError } = await supabase.functions.invoke(
        "conversion-ai",
        {
          body: { action: "banners", payload: feed },
        }
      );

      if (fnError || !aiData?.banner1) throw new Error("AI function failed");

      const result = aiData as Banners;
      setCachedBanners(result);
      setBanners(result);
    } catch (err) {
      console.error("UrgencyBanner: failed to load AI banners, using fallback", err);
      setBanners(FALLBACK_BANNERS);
    }
  }, []);

  useEffect(() => {
    if (!dismissed) loadBanners();
  }, [dismissed, loadBanners]);

  // Rotate banners every 4 seconds
  useEffect(() => {
    if (!banners) return;
    const interval = setInterval(() => {
      setActiveIndex((i) => (i + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, [banners]);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "true");
    } catch {
      // ignore
    }
  };

  if (dismissed || !banners) return null;

  const bannerList = [banners.banner1, banners.banner2, banners.banner3];

  return (
    <div
      className="sticky top-0 z-50 w-full"
      style={{
        background: "linear-gradient(90deg, hsl(174 62% 30%) 0%, hsl(200 60% 28%) 100%)",
      }}
      role="banner"
      aria-label="Platform activity update"
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
        {/* Rotating banner text */}
        <div className="flex-1 overflow-hidden text-center min-h-[1.25rem]">
          <AnimatePresence mode="wait">
            <motion.p
              key={activeIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="text-sm font-medium text-white leading-tight"
            >
              {bannerList[activeIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Dot indicators */}
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          {bannerList.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              aria-label={`Show banner ${i + 1}`}
              className={`w-1.5 h-1.5 rounded-full transition-opacity ${
                i === activeIndex ? "bg-white opacity-100" : "bg-white opacity-40"
              }`}
            />
          ))}
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          className="shrink-0 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UrgencyBanner;
